import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { Zap, Mail, Lock, ArrowLeft, Eye, EyeOff, KeyRound, Loader2, User, Bike, Phone, ChevronRight } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerLogin = () => {
  const navigate = useNavigate();
  const { loginCustomer, loginCustomerById } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Multiple accounts selection state
  const [showAccountSelection, setShowAccountSelection] = useState(false);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);

  // Password reset state
  const [resetStep, setResetStep] = useState(0); // 0: login, 1: request reset, 2: enter code, 3: new password
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await loginCustomer(formData.email, formData.password);
      
      // Check if requires account selection
      if (result.requires_selection) {
        setAvailableAccounts(result.customers);
        setShowAccountSelection(true);
        toast.info('Seleziona quale account vuoi usare');
        setIsLoading(false);
        return;
      }
      
      toast.success('Accesso effettuato con successo!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante l\'accesso';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAccount = async (accountId) => {
    setSelectedAccountId(accountId);
    setIsLoading(true);

    try {
      await loginCustomerById(accountId, formData.password);
      toast.success('Accesso effettuato con successo!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante l\'accesso';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowAccountSelection(false);
    setAvailableAccounts([]);
    setSelectedAccountId(null);
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API_URL}/auth/customer/forgot-password`, { email: resetEmail });
      toast.success('Codice di reset inviato alla tua email!');
      setResetStep(2);
    } catch (error) {
      toast.error('Errore nell\'invio del codice');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('La password deve essere di almeno 8 caratteri');
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${API_URL}/auth/customer/reset-password`, {
        email: resetEmail,
        codice: resetCode,
        new_password: newPassword
      });
      toast.success('Password aggiornata con successo!');
      setResetStep(0);
      setResetEmail('');
      setResetCode('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nel reset della password';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const renderLoginForm = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            id="email"
            type="email"
            placeholder="tu@esempio.com"
            className="pl-10 h-12"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            data-testid="login-email-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            placeholder="••••••••"
            className="pl-10 pr-10 h-12"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required
            data-testid="login-password-input"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <div className="text-right">
        <button 
          type="button" 
          onClick={() => setResetStep(1)}
          className="text-sm text-primary hover:underline"
          data-testid="forgot-password-link"
        >
          Password dimenticata?
        </button>
      </div>

      <Button 
        type="submit" 
        className="w-full h-12 font-bold"
        disabled={isLoading}
        data-testid="login-submit-btn"
      >
        {isLoading ? 'Accesso in corso...' : 'Accedi'}
      </Button>
    </form>
  );

  const renderAccountSelection = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <User className="w-12 h-12 mx-auto text-primary mb-2" />
        <h3 className="font-bold text-lg">Seleziona Account</h3>
        <p className="text-sm text-muted-foreground">
          Più account sono associati a questa email. Seleziona quale vuoi usare.
        </p>
      </div>

      <div className="space-y-2">
        {availableAccounts.map((account) => (
          <button
            key={account.id}
            onClick={() => handleSelectAccount(account.id)}
            disabled={isLoading && selectedAccountId === account.id}
            className="w-full p-4 border rounded-lg hover:bg-muted transition-colors text-left flex items-center justify-between group"
            data-testid={`account-option-${account.id}`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-5 h-5 text-primary" />
              </div>
              <div>
                <div className="font-medium">{account.cognome} {account.nome}</div>
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <Bike className="w-3 h-3" />
                  {account.modello_moto}
                </div>
                {account.telefono && (
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    {account.telefono}
                  </div>
                )}
              </div>
            </div>
            {isLoading && selectedAccountId === account.id ? (
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            ) : (
              <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
            )}
          </button>
        ))}
      </div>

      <Button 
        variant="outline"
        onClick={handleBackToLogin}
        className="w-full"
        disabled={isLoading}
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Torna al login
      </Button>
    </div>
  );

  const renderRequestResetForm = () => (
    <form onSubmit={handleRequestReset} className="space-y-4">
      <div className="text-center mb-4">
        <KeyRound className="w-12 h-12 mx-auto text-primary mb-2" />
        <h3 className="font-bold text-lg">Password Dimenticata</h3>
        <p className="text-sm text-muted-foreground">
          Inserisci la tua email per ricevere il codice di reset
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="reset-email">Email</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            id="reset-email"
            type="email"
            placeholder="tu@esempio.com"
            className="pl-10 h-12"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            required
            data-testid="reset-email-input"
          />
        </div>
      </div>

      <Button 
        type="submit" 
        className="w-full h-12 font-bold"
        disabled={isLoading}
        data-testid="send-reset-code-btn"
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Invio in corso...
          </>
        ) : 'Invia Codice'}
      </Button>

      <Button 
        type="button" 
        variant="ghost"
        className="w-full"
        onClick={() => setResetStep(0)}
      >
        Torna al login
      </Button>
    </form>
  );

  const renderCodeEntryForm = () => (
    <form onSubmit={(e) => { e.preventDefault(); setResetStep(3); }} className="space-y-4">
      <div className="text-center mb-4">
        <KeyRound className="w-12 h-12 mx-auto text-primary mb-2" />
        <h3 className="font-bold text-lg">Inserisci il Codice</h3>
        <p className="text-sm text-muted-foreground">
          Abbiamo inviato un codice a 6 cifre a <br/>
          <span className="font-medium text-foreground">{resetEmail}</span>
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="reset-code">Codice di Verifica</Label>
        <Input
          id="reset-code"
          type="text"
          placeholder="000000"
          className="h-12 text-center text-2xl tracking-widest font-mono"
          maxLength={6}
          value={resetCode}
          onChange={(e) => setResetCode(e.target.value.replace(/\D/g, ''))}
          required
          data-testid="reset-code-input"
        />
      </div>

      <Button 
        type="submit" 
        className="w-full h-12 font-bold"
        disabled={resetCode.length !== 6}
        data-testid="verify-code-btn"
      >
        Verifica Codice
      </Button>

      <div className="flex gap-2">
        <Button 
          type="button" 
          variant="ghost"
          className="flex-1"
          onClick={() => setResetStep(1)}
        >
          Cambia email
        </Button>
        <Button 
          type="button" 
          variant="ghost"
          className="flex-1"
          onClick={handleRequestReset}
          disabled={isLoading}
        >
          Reinvia codice
        </Button>
      </div>
    </form>
  );

  const renderNewPasswordForm = () => (
    <form onSubmit={handleResetPassword} className="space-y-4">
      <div className="text-center mb-4">
        <Lock className="w-12 h-12 mx-auto text-primary mb-2" />
        <h3 className="font-bold text-lg">Nuova Password</h3>
        <p className="text-sm text-muted-foreground">
          Inserisci la tua nuova password (min. 8 caratteri)
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="new-password">Nuova Password</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            id="new-password"
            type={showNewPassword ? "text" : "password"}
            placeholder="••••••••"
            className="pl-10 pr-10 h-12"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            data-testid="new-password-input"
          />
          <button
            type="button"
            onClick={() => setShowNewPassword(!showNewPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="confirm-password">Conferma Password</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            id="confirm-password"
            type={showConfirmPassword ? "text" : "password"}
            placeholder="••••••••"
            className="pl-10 pr-10 h-12"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            data-testid="confirm-password-input"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <Button 
        type="submit" 
        className="w-full h-12 font-bold"
        disabled={isLoading || newPassword.length < 8 || newPassword !== confirmPassword}
        data-testid="reset-password-btn"
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Aggiornamento...
          </>
        ) : 'Aggiorna Password'}
      </Button>

      <Button 
        type="button" 
        variant="ghost"
        className="w-full"
        onClick={() => {
          setResetStep(0);
          setResetEmail('');
          setResetCode('');
          setNewPassword('');
          setConfirmPassword('');
        }}
      >
        Annulla
      </Button>
    </form>
  );

  const renderCurrentStep = () => {
    // If showing account selection, render that instead
    if (showAccountSelection) {
      return renderAccountSelection();
    }
    
    switch (resetStep) {
      case 1:
        return renderRequestResetForm();
      case 2:
        return renderCodeEntryForm();
      case 3:
        return renderNewPasswordForm();
      default:
        return renderLoginForm();
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Image */}
      <div className="hidden lg:block relative">
        <img 
          src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&h=1600&fit=crop"
          alt="NIU Scooter"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/30"></div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-white max-w-md">
            <Zap className="w-16 h-16 text-primary mb-6" />
            <h1 className="font-heading text-4xl font-black mb-4">NIU Service Italia</h1>
            <p className="text-white/80 text-lg">
              Accedi al tuo account per gestire le prenotazioni dei tagliandi.
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-background">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Torna alla home
          </Link>

          <Card className="border-border/50">
            <CardHeader className="space-y-1">
              <div className="flex items-center gap-2 lg:hidden mb-4">
                <Zap className="w-8 h-8 text-primary" />
                <span className="font-heading font-bold text-xl">NIU Service</span>
              </div>
              <CardTitle className="font-heading text-2xl">
                {showAccountSelection ? 'Seleziona Account' : (resetStep === 0 ? 'Accedi' : 'Reset Password')}
              </CardTitle>
              <CardDescription>
                {showAccountSelection 
                  ? 'Scegli quale account vuoi usare'
                  : (resetStep === 0 
                    ? 'Inserisci le tue credenziali per accedere'
                    : 'Segui i passaggi per reimpostare la password')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {renderCurrentStep()}

              {resetStep === 0 && (
                <div className="mt-6 text-center text-sm">
                  <span className="text-muted-foreground">Non hai un account? </span>
                  <Link to="/register" className="text-primary font-medium hover:underline" data-testid="go-to-register-link">
                    Registrati
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CustomerLogin;
